import { useQueryClient } from '@tanstack/react-query'
import { Form, Input, message, Modal, Select } from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'
import { UserSearchSelect } from '@/components/common/UserSearchSelect'
import { useClusters } from '@/hooks/useClusters'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'

interface CreatePublicIpModalProps {
  open: boolean
  onClose: () => void
}

export function CreatePublicIpModal({
  open,
  onClose,
}: CreatePublicIpModalProps) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [creating, setCreating] = useState(false)
  const clusters = useClusters()

  const handleCreate = async () => {
    try {
      const values = await form.validateFields()
      setCreating(true)
      const result = await client.POST('/v1/admin/public_ips', { body: values })
      unwrapApiData(result, 'Failed to create public IP')
      void message.success('Public IP created')
      form.resetFields()
      onClose()
      invalidateAdminCaches(queryClient)
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        void message.error(error.message)
      }
    } finally {
      setCreating(false)
    }
  }

  return (
    <Modal
      title="Create Public IP"
      open={open}
      onOk={handleCreate}
      okText="Create Public IP"
      onCancel={onClose}
      confirmLoading={creating}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="userId" label="User" rules={[{ required: true }]}>
          <UserSearchSelect onChange={(v) => form.setFieldValue('userId', v)} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="IP name" />
        </Form.Item>
        <Form.Item
          name="clusterId"
          label="Cluster"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select cluster">
            {clusters.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {c.name ?? c.id}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="addressType"
          label="Address Type"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select type">
            <Select.Option value="V4">IPv4</Select.Option>
            <Select.Option value="V6">IPv6</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  )
}
